import styled from '@emotion/styled';
import { isSafeInlineSvg, type BenefitIconCustomizationAsset, type ReferencedAssetPolicy } from '../customization';
import { Text } from '../atoms/Text';

export interface BenefitIconGridProps extends ReferencedAssetPolicy {
  benefits?: readonly BenefitIconCustomizationAsset[];
  className?: string;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: ${({ theme }) => theme.space.lg};
  width: 100%;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  text-align: center;
`;

const IconFrame = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg,
  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
`;

const IconImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

function canRenderBenefit(asset: BenefitIconCustomizationAsset, allowReferencedAssets: boolean): boolean {
  if (asset.licenseRisk === 'blocked') return false;
  if (isSafeInlineSvg(asset.inlineSvg)) return true;
  if (asset.source === 'referenced' && !allowReferencedAssets) return false;
  return Boolean(asset.src);
}

export function BenefitIconGrid({
  benefits = [],
  allowReferencedAssets = false,
  className,
}: BenefitIconGridProps) {
  const renderableBenefits = benefits.filter((benefit) => canRenderBenefit(benefit, allowReferencedAssets));

  if (renderableBenefits.length === 0) {
    return null;
  }

  return (
    <Grid className={className} role="list">
      {renderableBenefits.map((benefit) => (
        <Item key={benefit.assetId} role="listitem">
          <IconFrame>
            {isSafeInlineSvg(benefit.inlineSvg) ? (
              <span
                role={benefit.label ? 'presentation' : 'img'}
                aria-hidden={benefit.label ? true : undefined}
                aria-label={benefit.label ? undefined : benefit.alt}
                dangerouslySetInnerHTML={{ __html: benefit.inlineSvg }}
              />
            ) : benefit.src ? (
              <IconImage src={benefit.src} alt={benefit.label ? '' : benefit.alt ?? ''} loading="lazy" />
            ) : null}
          </IconFrame>
          {benefit.label && (
            <Text variant="body" weight="bold">
              {benefit.label}
            </Text>
          )}
          {benefit.body && (
            <Text variant="body" size="small">
              {benefit.body}
            </Text>
          )}
        </Item>
      ))}
    </Grid>
  );
}
